import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, Loader2, Image as ImageIcon, Info } from "lucide-react";
import { toast } from "sonner";
import imageCompression from 'browser-image-compression';
import IssuerLayout from "@/components/IssuerLayout";
import CertificateEditor from "@/components/CertificateEditor";
import FieldTooltip from "@/components/FieldTooltip";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TemplateEditor = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [logoPreview, setLogoPreview] = useState(null);
  const [customBackground, setCustomBackground] = useState(null);
  const [useCustomBackground, setUseCustomBackground] = useState(false);
  const [templateLayout, setTemplateLayout] = useState(null);
  const [formData, setFormData] = useState({
    template_name: "My Custom Template",
    template_id: "",
    instructor_name: "",
    logo_base64: ""
  });

  useEffect(() => {
    const token = localStorage.getItem('issuer_token');
    if (!token) {
      navigate("/issuer/login");
      return;
    }
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('issuer_token');
      const response = await axios.get(`${API}/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Handle paginated response
      const templateData = response.data.items || response.data;
      setTemplates(templateData);
      if (templateData.length > 0) {
        setFormData(prev => ({ ...prev, template_id: templateData[0].id }));
      }
    } catch (err) {
      toast.error("Failed to load templates");
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        // Compress logo
        const options = {
          maxSizeMB: 0.5,
          maxWidthOrHeight: 500,
          useWebWorker: true
        };
        const compressedFile = await imageCompression(file, options);
        
        const reader = new FileReader();
        reader.onloadend = () => {
          setLogoPreview(reader.result);
          setFormData(prev => ({ ...prev, logo_base64: reader.result }));
          toast.success(`Logo compressed: ${(compressedFile.size / 1024).toFixed(1)}KB`);
        };
        reader.readAsDataURL(compressedFile);
      } catch (error) {
        toast.error("Failed to upload logo");
      }
    }
  };

  const handleBackgroundUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        // Compress background - keep quality but reduce size
        const options = {
          maxSizeMB: 1,
          maxWidthOrHeight: 1056, // Match canvas width
          useWebWorker: true
        };
        const compressedFile = await imageCompression(file, options);
        
        const reader = new FileReader();
        reader.onloadend = () => {
          setCustomBackground(reader.result);
          setUseCustomBackground(true);
          toast.success(`Background uploaded: ${(compressedFile.size / 1024).toFixed(1)}KB`);
        };
        reader.readAsDataURL(compressedFile);
      } catch (error) {
        toast.error("Failed to upload background");
      }
    }
  };

  const handleSaveTemplate = (templateData) => {
    setTemplateLayout(templateData);
    toast.success("Template layout saved!");
  };

  const handleFinalSave = async () => {
    if (!templateLayout) {
      toast.error("Please save the template layout first using the 'Save Template' button in the editor");
      return;
    }
    
    setLoading(true);

    try {
      const token = localStorage.getItem('issuer_token');
      // For now, we'll just save to localStorage - can add backend endpoint later
      const savedTemplates = JSON.parse(localStorage.getItem('saved_templates') || '[]');
      const newTemplate = {
        id: Date.now().toString(),
        name: formData.template_name,
        template_id: formData.template_id,
        layout: templateLayout,
        instructor_name: formData.instructor_name,
        logo_base64: formData.logo_base64,
        custom_background: useCustomBackground ? customBackground : null,
        created_at: new Date().toISOString()
      };
      savedTemplates.push(newTemplate);
      localStorage.setItem('saved_templates', JSON.stringify(savedTemplates));
      
      toast.success("Template saved to your account!");
      navigate("/issuer/create");
    } catch (err) {
      toast.error("Failed to save template");
    } finally {
      setLoading(false);
    }
  };

  const selectedTemplate = templates.find(t => t.id === formData.template_id);
  const activeBackground = useCustomBackground ? customBackground : selectedTemplate?.background_url;

  return (
    <IssuerLayout>
      <div data-testid="template-editor-page">
        <div className="mb-8">
          <h1 className="text-4xl font-medium text-slate-900 mb-2" style={{fontFamily: 'Playfair Display'}}>Template Editor</h1>
          <p className="text-slate-600">Design and customize your certificate templates</p>
        </div>

        {/* Instructions */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-8">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-blue-900 mb-2">How to Use the Template Editor</h3>
              <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                <li><strong>Design your layout:</strong> Click any text element (title, recipient name, date, etc.) to select it, then drag to reposition.</li>
                <li><strong>Customize styling:</strong> Use the Properties Panel on the right to change font size, color, family, and alignment.</li>
                <li><strong>Resize elements:</strong> Select an element and drag the corner handles to resize text areas or images.</li>
                <li><strong>Add your branding:</strong> Upload a logo and position it anywhere on the certificate.</li>
                <li><strong>Custom backgrounds:</strong> Upload your own certificate background (11x8.5") or choose from presets.</li>
                <li><strong>Placeholder text:</strong> Text shown in brackets like [Recipient Name] or [Course Name] will be replaced with actual data when certificates are issued.</li>
                <li><strong>Print safe zone:</strong> Keep important content inside the blue dashed line for proper printing.</li>
              </ul>
              <p className="text-sm text-blue-700 mt-3 font-medium">💡 Tip: You're designing the template layout and styling. Actual recipient data will be added automatically when you issue certificates.</p>
            </div>
          </div>
        </div>

        {/* Settings Panel */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 mb-8">
          <h2 className="text-lg font-semibold text-slate-900 mb-4" style={{fontFamily: 'Playfair Display'}}>Template Settings</h2>
          
          <div className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="template-name" className="text-sm font-medium text-slate-700 mb-1.5 block">
                  Template Name
                  <FieldTooltip 
                    fieldName="Template Name"
                    description="This is the name of your template for your own reference. You can save multiple templates and reuse them for different courses or programs."
                  />
                </Label>
                <Input
                  id="template-name"
                  type="text"
                  value={formData.template_name}
                  onChange={(e) => setFormData({...formData, template_name: e.target.value})}
                  placeholder="My Custom Template"
                  className="h-11 rounded-lg border-slate-200"
                  data-testid="template-name-input"
                />
              </div>

              <div>
                <Label htmlFor="default-instructor" className="text-sm font-medium text-slate-700 mb-1.5 block">
                  Default Instructor
                  <FieldTooltip 
                    fieldName="Instructor Name"
                    description="Enter the default instructor name for this template. You can edit the styling and position in the editor below. The actual instructor name will appear on certificates when issued."
                  />
                </Label>
                <Input
                  id="default-instructor"
                  type="text"
                  value={formData.instructor_name}
                  onChange={(e) => setFormData({...formData, instructor_name: e.target.value})}
                  placeholder="Dr. Jane Smith"
                  className="h-11 rounded-lg border-slate-200"
                  data-testid="instructor-input"
                />
              </div>
            </div>

            {/* Background Options */}
            <div className="border-t border-slate-200 pt-4">
              <h3 className="text-sm font-semibold text-slate-900 mb-3">Background Options</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="background-template" className="text-sm font-medium text-slate-700 mb-1.5 block">
                    Preset Templates
                    <FieldTooltip 
                      fieldName="Background Template"
                      description="Choose from professionally designed certificate backgrounds, or upload your own custom background below. You can position and style all text elements on top of the background."
                    />
                  </Label>
                  <Select 
                    value={formData.template_id} 
                    onValueChange={(value) => {
                      setFormData({...formData, template_id: value});
                      setUseCustomBackground(false);
                      // Load saved template layout
                      const newTemplate = templates.find(t => t.id === value);
                      if (newTemplate?.layout) {
                        setTemplateLayout(newTemplate.layout);
                      } else {
                        setTemplateLayout(null);
                      }
                    }}
                    disabled={useCustomBackground}
                  >
                    <SelectTrigger className="h-11 rounded-lg border-slate-200" data-testid="background-select">
                      <SelectValue placeholder="Select background" />
                    </SelectTrigger>
                    <SelectContent>
                      {templates.map(template => (
                        <SelectItem key={template.id} value={template.id} data-testid={`bg-option-${template.id}`}>
                          {template.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="custom-bg-upload" className="text-sm font-medium text-slate-700 mb-1.5 block">
                    Upload Custom Background
                    <span className="text-xs text-slate-500 ml-2">(11x8.5", max 1MB)</span>
                    <FieldTooltip 
                      fieldName="Custom Background"
                      description="Upload your own certificate background image (11x8.5 inches, landscape). After uploading, you can drag and resize the background to fit perfectly. Images are automatically compressed to keep file sizes low."
                    />
                  </Label>
                  <label className="cursor-pointer">
                    <input
                      id="custom-bg-upload"
                      type="file"
                      accept="image/jpeg,image/jpg,image/png"
                      onChange={handleBackgroundUpload}
                      className="hidden"
                      data-testid="custom-bg-input"
                    />
                    <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 hover:bg-blue-100 rounded-lg border border-blue-200 transition-colors h-11">
                      <ImageIcon className="h-4 w-4 text-blue-600" />
                      <span className="text-sm text-blue-700 font-medium">
                        {customBackground ? 'Change Custom BG' : 'Upload Custom BG'}
                      </span>
                    </div>
                  </label>
                  {customBackground && (
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setCustomBackground(null);
                        setUseCustomBackground(false);
                      }}
                      className="text-xs text-red-600 hover:text-red-700 mt-1 h-auto p-1"
                      data-testid="clear-custom-bg-btn"
                    >
                      Clear custom background
                    </Button>
                  )}
                </div>
              </div>

              {useCustomBackground && (
                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-xs text-blue-900">
                    <strong>Using custom background.</strong> Your uploaded image will be used instead of preset templates.
                  </p>
                </div>
              )}
            </div>

            {/* Logo Upload */}
            <div className="border-t border-slate-200 pt-4">
              <Label htmlFor="logo-upload" className="text-sm font-medium text-slate-700 mb-1.5 block">
                Logo Upload
                <FieldTooltip 
                  fieldName="Logo"
                  description="Upload your organization's logo. After uploading, you can position it anywhere on the certificate and resize it using the corner handles in the editor below."
                />
              </Label>
              <div className="flex items-center gap-4">
                <label className="cursor-pointer">
                  <input
                    id="logo-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                    data-testid="logo-input"
                  />
                  <div className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg border border-slate-200 transition-colors h-11">
                    <Upload className="h-4 w-4 text-slate-600" />
                    <span className="text-sm text-slate-700">{logoPreview ? 'Change Logo' : 'Upload Logo'}</span>
                  </div>
                </label>
                {logoPreview && (
                  <div className="h-11 w-20 border border-slate-200 rounded-lg overflow-hidden bg-white flex items-center justify-center">
                    <img src={logoPreview} alt="Logo preview" className="max-h-full max-w-full object-contain" />
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="mt-6 flex gap-3">
            <Button
              onClick={handleFinalSave}
              disabled={loading || !templateLayout}
              className="bg-slate-900 text-white hover:bg-slate-800 rounded-lg px-6 py-2"
              data-testid="final-save-btn"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save to My Templates
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/issuer/dashboard")}
              className="rounded-lg px-6 py-2"
              data-testid="cancel-btn"
            >
              Cancel
            </Button>
          </div>
        </div>

        {/* Certificate Editor */}
        {selectedTemplate && (
          <CertificateEditor
            key={formData.template_id}
            templateBackground={activeBackground ? (activeBackground.startsWith('data:') ? activeBackground : `${API}/proxy-image?url=${encodeURIComponent(activeBackground)}`) : null}
            onSave={handleSaveTemplate}
            logoPreview={logoPreview}
            instructorName={formData.instructor_name}
            isCustomBackground={useCustomBackground}
            initialLayout={selectedTemplate?.layout || templateLayout}
          />
        )}
      </div>
    </IssuerLayout>
  );
};

export default TemplateEditor;
